import {useState} from '@wordpress/element';
import {Elements, PaymentElement} from "@stripe/react-stripe-js";
import {StoreNotice} from '@woocommerce/blocks-checkout';
import {getSetting} from '@woocommerce/settings';
import {initStripe as loadStripe, getPaymentElementTerms} from '@wc-stripe/blocks-utils'
import {useCreatePaymentMethod} from "../hooks/use-create-payment-method";
import {useProcessCheckoutError} from "../hooks/use-process-checkout-error";
import {useProcessCheckoutSuccess} from "../hooks/use-process-checkout-success";
import {useElementOptions} from "../hooks/use-element-options";

/**
 * Return true if the local payment method can be used.
 * @param {String} gatewayId
 * @returns {function({billingAddress: *, [p: string]: *}): *}
 */
export const canMakePayment = (gatewayId) => ({cart, billingAddress}) => {
    const data = getSetting(`${gatewayId}_data`);
    const {
        currencies = [],
        allowedCountries = '',
        exceptCountries = [],
        limitedCountries = [],
        specificCountries = [],
    } = data;
    const {country = ''} = billingAddress;

    let currency = cart.cartTotals.currency_code?.toLowerCase();
    if (!currency || currency?.length < 3) {
        currency = getSetting('stripeGeneralData')?.currency?.toLowerCase();
    }

    if (currencies.length && !currencies.includes(currency.toUpperCase())) {
        return false;
    }

    if (allowedCountries === 'all_except') {
        if (exceptCountries.includes(country)) {
            return false;
        }
        if (limitedCountries.length && !limitedCountries.includes(country)) {
            return false;
        }
    } else if (allowedCountries === 'specific') {
        if (!specificCountries.includes(country)) {
            return false;
        }
    } else {
        if (limitedCountries.length && !limitedCountries.includes(country)) {
            return false;
        }
    }

    const extensions = cart?.extensions || {};
    return !!extensions?.wc_stripe?.cart?.paymentMethods?.[gatewayId]?.available;
}

export const LocalPaymentIntentContent = (props) => {
    const {data, billing, cartData, mandate = false} = props;
    const gatewayId = data.gatewayId;
    const {extensions} = cartData;
    const {cartTotal, currency} = billing;

    const ELEMENT_OPTIONS = useElementOptions({
        cartTotal,
        currency,
        elementOptions: extensions[gatewayId]?.elementOptions
    });

    return (
        <Elements stripe={loadStripe} options={ELEMENT_OPTIONS}>
            <PaymentMethodContent {...props}/>
        </Elements>
    )
}

const PaymentMethodContent = (
    {
        data,
        billing,
        emitResponse,
        shouldSavePayment,
        eventRegistration,
        activePaymentMethod,
        shouldCreatePaymentMethod = false
    }) => {
    const name = data.name;
    const gatewayId = data.gatewayId;
    const {billingAddress} = billing;
    const {onCheckoutSuccess, onCheckoutFail} = eventRegistration;
    const [loadError, setLoadError] = useState(false);

    const PAYMENT_ELEMENT_OPTIONS = {
        defaultValues: {
            billingDetails: {
                phone: billingAddress.phone,
                email: billingAddress.email,
                name: `${billingAddress.first_name} ${billingAddress.last_name}`,
                address: {
                    country: billingAddress.country,
                    state: billingAddress.state
                }
            }
        },
        fields: {
            billingDetails: {
                address: 'never',
                name: 'never',
                email: 'never',
                phone: !!billingAddress?.phone ? 'never' : 'auto'
            }
        },
        wallets: {applePay: 'never', googlePay: 'never'},
        terms: getPaymentElementTerms(data.termsDisplayRule),
        ...data.paymentElementOptions
    }


    const {clearPaymentMethodData} = useCreatePaymentMethod({
        name,
        gatewayId,
        emitResponse,
        billingAddress,
        shouldSavePayment,
        eventRegistration,
        activePaymentMethod,
        shouldCreatePaymentMethod
    });

    useProcessCheckoutError({
        emitResponse,
        subscriber: onCheckoutFail,
        messageContext: emitResponse.noticeContexts.PAYMENTS,
        clearPaymentMethodData
    });

    useProcessCheckoutSuccess({
        name,
        emitResponse,
        billingAddress,
        onCheckoutSuccess,
        activePaymentMethod
    });

    const onChange = (event) => {
    };

    const onLoadError = response => {
        const {error} = response;
        setLoadError(error);
    }

    if (loadError) {
        const message = getSetting('stripeErrorMessages').payment_unavailable;
        return (
            <StoreNotice
                status={'warning'}
                isDismissible={false}>
                {message.replace('%s', loadError.message)}
            </StoreNotice>
        )
    }

    return (
        <PaymentElement
            options={PAYMENT_ELEMENT_OPTIONS}
            onChange={onChange}
            onLoadError={onLoadError}/>
    )
}